import { Box, Typography, Stack, Button, Container, Grid, Chip, Link as MuiLink } from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import QrCodeScannerOutlinedIcon from "@mui/icons-material/QrCodeScannerOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import LockPersonOutlinedIcon from "@mui/icons-material/LockPersonOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import MarketingLayout from "@/components/Marketing/MarketingLayout";
import FaqSection from "@/components/Marketing/FaqSection";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { trackCtaClick } from "@/utils/analytics";

const FEATURES = [
  { icon: <Inventory2OutlinedIcon fontSize="large" />, title: "Asset Tracking", body: "Track every laptop, monitor, and software license from purchase to retirement, with full history in one place.", href: "/features/asset-tracking" },
  { icon: <AssignmentTurnedInOutlinedIcon fontSize="large" />, title: "Employee Assignment", body: "Assign equipment to employees, route transfers and returns through approval, and always know who has what.", href: "/features/asset-assignment" },
  { icon: <BuildOutlinedIcon fontSize="large" />, title: "Warranty & Lifecycle", body: "Track warranty and end-of-life dates for every asset so replacements get planned, not discovered.", href: "/features/warranty-tracking" },
  { icon: <QrCodeScannerOutlinedIcon fontSize="large" />, title: "QR Code Scanning", body: "Scan any asset's tag with a phone camera to instantly see its details and full history.", href: "/features/qr-code-asset-tracking" },
  { icon: <LockPersonOutlinedIcon fontSize="large" />, title: "Microsoft Entra ID SSO", body: "Sign in with the Microsoft 365 account your team already uses - or plain email and password.", href: "/features/microsoft-entra-sso" },
  { icon: <AssessmentOutlinedIcon fontSize="large" />, title: "Reports & Exports", body: "Break down your fleet by department, employee, or category and export to Excel or PDF.", href: "/features/it-asset-management" },
  { icon: <NotificationsActiveOutlinedIcon fontSize="large" />, title: "Real-Time Notifications", body: "Approvals, assignments, and requests show up the moment they happen - no refreshing, no missed handoffs.", href: "/features" },
  { icon: <HistoryOutlinedIcon fontSize="large" />, title: "Full Audit Trail", body: "Every create, delete, assignment, and change is logged automatically, so you always know who did what and when.", href: "/security" }
];

const STEPS = [
  { step: "1", title: "Add your assets", body: "Add assets one at a time or import your whole inventory from a spreadsheet - categories, departments, and locations come ready to go." },
  { step: "2", title: "Assign & track", body: "Hand assets to real people from your company directory, transfer them, and always know exactly where they are." },
  { step: "3", title: "Stay in control", body: "Route changes through approvals, log maintenance, and pull reports whenever leadership or an auditor asks." }
];

const FAQ = [
  { question: "Who is AssetOps built for?", answer: "Growing businesses that have outgrown a spreadsheet for tracking IT equipment but don't need (or want to pay for) an enterprise asset management platform built for much larger companies." },
  { question: "Is AssetOps free?", answer: "Yes, AssetOps is currently free to use while we're in early access - no credit card required to sign up." },
  { question: "Do we need Microsoft 365 to use AssetOps?", answer: "No. Companies with Microsoft 365 can sign in with Microsoft Entra ID single sign-on, but AssetOps also supports plain email and password accounts for companies that don't use Microsoft 365." },
  { question: "How long does setup take?", answer: "Most teams are tracking real assignments the same day - import your existing spreadsheet in one step, and start assigning equipment immediately." }
];

function goToSignup(source: string) {
  trackCtaClick(source);
  window.location.href = "/login?signup=1";
}

export default function LandingPage() {
  useDocumentTitle("AssetOps by Athriv | IT Asset Management Software");

  return (
    <MarketingLayout>
      {/* Hero */}
      <Container maxWidth="md" sx={{ textAlign: "center", py: { xs: 6, md: 10 } }}>
        <Chip label="IT Asset Management Software for Modern Businesses" size="small" color="primary" variant="outlined" sx={{ mb: 3 }} />
        <Typography component="h1" variant="h2" sx={{ fontFamily: '"Manrope", sans-serif', fontWeight: 800, fontSize: { xs: "2.2rem", md: "3.2rem" } }}>
          Track Every Asset.<br />From Purchase to Retirement.
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mt: 2, fontWeight: 400 }}>
          AssetOps tracks your laptops, licenses, and equipment for their entire lifetime - who has each one,
          where it's been, its warranty status, and its full history - with Microsoft Entra ID sign-in built in.
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center" sx={{ mt: 4 }}>
          <Button size="large" variant="contained" onClick={() => goToSignup("start_free_hero")}>Start Free</Button>
          <Button size="large" variant="outlined" href="/contact" onClick={() => trackCtaClick("book_demo_hero")}>Book a Demo</Button>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Free during early access. No credit card required. Or{" "}
          <MuiLink component="a" href="/login?demo=1" onClick={() => trackCtaClick("view_live_demo_hero")}>
            explore a live demo instantly
          </MuiLink> - no signup needed.
        </Typography>
      </Container>

      {/* Features */}
      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 8 } }}>
        <Stack spacing={1} alignItems="center" textAlign="center" sx={{ mb: 5 }}>
          <Typography component="h2" variant="h4" sx={{ fontFamily: '"Manrope", sans-serif', fontWeight: 800 }}>
            Everything you need to run a tight ship
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560 }}>
            From day-one setup to year-end audits, AssetOps covers the full life of every asset your team owns.
          </Typography>
        </Stack>
        <Grid container spacing={3}>
          {FEATURES.map((f) => (
            <Grid key={f.title} size={{ xs: 12, sm: 6, md: 3 }}>
              <Stack component="a" href={f.href} spacing={1.5} alignItems="flex-start" sx={{ textDecoration: "none", color: "inherit", display: "flex" }}>
                <Box
                  sx={{
                    width: 56, height: 56, borderRadius: "14px", bgcolor: "action.hover",
                    display: "flex", alignItems: "center", justifyContent: "center", color: "primary.main"
                  }}
                >
                  {f.icon}
                </Box>
                <Typography variant="subtitle1" fontWeight={700}>{f.title}</Typography>
                <Typography variant="body2" color="text.secondary">{f.body}</Typography>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* How it works */}
      <Box sx={{ bgcolor: "background.paper", borderTop: "1px solid", borderBottom: "1px solid", borderColor: "divider", py: { xs: 6, md: 10 } }}>
        <Container maxWidth="xl">
          <Stack spacing={1} alignItems="center" textAlign="center" sx={{ mb: 5 }}>
            <Typography component="h2" variant="h4" sx={{ fontFamily: '"Manrope", sans-serif', fontWeight: 800 }}>Up and running in three steps</Typography>
          </Stack>
          <Grid container spacing={4}>
            {STEPS.map((s) => (
              <Grid key={s.step} size={{ xs: 12, md: 4 }}>
                <Stack spacing={1.5} alignItems="center" textAlign="center">
                  <Box
                    sx={{
                      width: 44, height: 44, borderRadius: "50%", bgcolor: "primary.main",
                      color: "primary.contrastText", display: "flex", alignItems: "center", justifyContent: "center",
                      fontFamily: '"Manrope", sans-serif', fontWeight: 800, fontSize: "1.1rem"
                    }}
                  >
                    {s.step}
                  </Box>
                  <Typography variant="subtitle1" fontWeight={700}>{s.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>{s.body}</Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <FaqSection items={FAQ} />

      {/* Closing CTA */}
      <Box sx={{ bgcolor: "primary.main", color: "primary.contrastText", py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Typography variant="h4" sx={{ fontFamily: '"Manrope", sans-serif', fontWeight: 800, mb: 1.5 }}>
            Ready to see where your assets really are?
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.85, mb: 4 }}>
            Set up your organization in minutes - no spreadsheets required.
          </Typography>
          <Button
            size="large" variant="contained" onClick={() => goToSignup("start_free_closing")}
            sx={{ bgcolor: "background.paper", color: "primary.main", "&:hover": { bgcolor: "background.default" } }}
          >
            Start Free
          </Button>
        </Container>
      </Box>
    </MarketingLayout>
  );
}
